// @flow

import React, { useCallback, useState, type Node } from "react";
import { useDispatch } from "react-redux";
import * as R from "ramda";
import { DndContext, closestCenter } from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  arrayMove,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import Remove from "./Remove";
import { SelectedStatusWrapper, List } from "./styles";
import Icons from "src/icons";
import { setWorkflowBuiderAttributes } from "src/actions/workflows";
import type { UpdateStatusFunction } from "src/components/Manage/Builder/Status";
import type { WorkflowStatus } from "src/types";

type Props = {
  value: Array<number>,
  search: string,
  statuses: Array<WorkflowStatus>,
  handleDeleteStatus: Function,
  setSearch: Function,
  handleSearch: Function,
  handleEditStatus: Function,
  tempName: string,
  setTempName: Function,
  updateStatusData: UpdateStatusFunction,
};

type SelectionProps = {
  children?: Node,
  search: string,
  statuses: Array<WorkflowStatus>,
  handleDeleteStatus: Function,
  setSearch: Function,
  handleSearch: Function,
  handleEditStatus: Function,
  sortedValue: Array<number>,
  tempName: string,
  setTempName: Function,
  updateStatusData: UpdateStatusFunction,
};

const Statuses = ({
  id,
  statuses,
  handleDeleteStatus,
  setSearch,
  handleSearch,
  handleEditStatus,
  search,
  tempName,
  setTempName,
  updateStatusData,
}) => {
  const [statusChangeModal, setStatusChangeModal] = useState(false);
  const data = R.find(R.propEq("id", id))(statuses || []);

  const { listeners, setNodeRef, setActivatorNodeRef, transform, transition } =
    useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <List ref={setNodeRef} style={style} statusModal={statusChangeModal}>
      <div ref={setActivatorNodeRef} {...listeners}>
        <Icons type="dottedSort" />
      </div>
      <Remove
        id={id}
        handleDeleteStatus={handleDeleteStatus}
        setSearch={setSearch}
        handleSearch={handleSearch}
        handleEditStatus={handleEditStatus}
        search={search}
        tempName={tempName}
        setTempName={setTempName}
        statusChangeModal={statusChangeModal}
        setStatusChangeModal={setStatusChangeModal}
        data={data}
        updateStatusData={updateStatusData}
      />
    </List>
  );
};

const Selection = ({
  children,
  search,
  statuses,
  handleDeleteStatus,
  setSearch,
  handleSearch,
  handleEditStatus,
  sortedValue,
  tempName,
  setTempName,
  updateStatusData,
}: SelectionProps) => (
  <SortableContext items={sortedValue} strategy={verticalListSortingStrategy}>
    <SelectedStatusWrapper>
      {(sortedValue || []).map((id, index) => {
        return (
          <Statuses
            id={id}
            key={`item-${index}`}
            statuses={statuses}
            handleDeleteStatus={handleDeleteStatus}
            setSearch={setSearch}
            handleSearch={handleSearch}
            handleEditStatus={handleEditStatus}
            search={search}
            tempName={tempName}
            setTempName={setTempName}
            updateStatusData={updateStatusData}
          />
        );
      })}
      {children}
    </SelectedStatusWrapper>
  </SortableContext>
);

const SortableComponent = ({
  value,
  search,
  statuses,
  handleDeleteStatus,
  setSearch,
  handleSearch,
  handleEditStatus,
  tempName,
  setTempName,
  updateStatusData,
}: Props) => {
  const dispatch = useDispatch();

  const handleDragEnd = useCallback(
    ({ active, over }) => {
      if (!over || active.id === over.id) return;
      const oldIndex = statuses.findIndex((s) => s.id === active.id);
      const newIndex = statuses.findIndex((s) => s.id === over.id);
      const newSort = arrayMove(statuses, oldIndex, newIndex);
      dispatch(
        setWorkflowBuiderAttributes({
          status: newSort,
        }),
      );
    },
    [statuses],
  );

  return (
    <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <Selection
        sortedValue={value}
        statuses={statuses}
        handleDeleteStatus={handleDeleteStatus}
        setSearch={setSearch}
        handleSearch={handleSearch}
        handleEditStatus={handleEditStatus}
        search={search}
        tempName={tempName}
        setTempName={setTempName}
        updateStatusData={updateStatusData}
      />
    </DndContext>
  );
};

export default SortableComponent;
